import { useState, useEffect, useMemo } from "react";
import { observer, Observer } from "mobx-react";
import { makeAutoObservable } from "mobx";
import { NumericFormat } from "react-number-format";
import TaskActuals from "./TaskActuals";
import TaskAnalysis from "./TaskAnalysis";
import TaskBaselines from "./TaskBaselines";
import TaskPeriodWeights from "./TaskPeriodWeights";
import TaskDistributionPicker from "./TaskDistributionPicker";
import ForecastMethodPicker from "./ForecastMethodPicker";
import { getUpdatedCost } from "../util/TaskUtil";
import {
  Paper, TextField, ButtonGroup, Button, Box,
  Tooltip, IconButton, Typography
} from "@mui/material";
import { debounce } from '@mui/material/utils';
import { UpdateTask } from "../operations/TaskOperations"
import EditIcon from "@mui/icons-material/Edit";
import validator from "validator";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useViewState } from "../model/ViewState";
import { useProjectStore } from "../model/ProjectStore";
import Task from "../model/Task";
import { useSnackbar } from "notistack";
import { runInAction } from "mobx";
import Grid from '@mui/material/Grid';
import { DateTime } from "luxon";
import { GetFAC } from "../operations/TaskOperations";

class TaskPanelFormData {
  plannedStartDate = DateTime.min;
  plannedEndDate = DateTime.min;
  actualStartDate = DateTime.min;
  actualEndDate = DateTime.min;

  constructor(task) {
    this.plannedStartDate = DateTime.fromISO(task.plannedStartDate);
    this.plannedEndDate = DateTime.fromISO(task.plannedEndDate);
    this.actualStartDate = DateTime.fromISO(task.actualStartDate);
    this.actualEndDate = DateTime.fromISO(task.actualEndDate);
    makeAutoObservable(this);
  }
}

const View = ({ task, tab, classes }) => {
  if (tab === "analysis") {
    return <TaskAnalysis task={task} classes={classes} />;
  } else if (tab === "baseline") {
    return <TaskBaselines task={task} classes={classes} readOnly={false} />;
  } else if (tab === "periodWeights") {
    return <TaskPeriodWeights task={task} classes={classes} />;
  } else {
    return <TaskActuals task={task} readOnly={false} />;
  }
};

const updateParentPlannedCost = (task) => {
  let parentTaskPackage = null;
  if (task.code !== "") {
    parentTaskPackage = task.getParentTaskPackage();
  }
  if (parentTaskPackage !== null && parentTaskPackage !== undefined) {
    parentTaskPackage.plannedCost = getUpdatedCost(parentTaskPackage);
    updateParentPlannedCost(parentTaskPackage);
  }
};

const TaskPanel = ({ selectedTask }) => {
  const { enqueueSnackbar } = useSnackbar();
  const view = useViewState();

  const project = useProjectStore();
  const [FACValues, setFACValues] = useState({ None: 0 });
  const [readOnlyMode, setReadOnlyMode] = useState(false);

  const formData = new TaskPanelFormData(selectedTask);

  const handleChangePlannedStartDate = () => {
    const initialEndDate = new Date(selectedTask.plannedEndDate);
    const dateDifferential = new Date(selectedTask.plannedStartDate) - new Date(selectedTask.plannedStartDate);

    runInAction(() => {
      UpdateTask(project, selectedTask, (result) => {
        let newTask = Task.fromJS(project, result, selectedTask.parent);
        const tasks = selectedTask.getParentTaskPackage().tasks;
        let idx = tasks.findIndex(t => t.id == selectedTask.id);
        tasks[idx] = newTask;
        selectedTask = newTask;
      }).catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      })
    });
  }

  useEffect(() => {
    GetFAC(selectedTask.id,
      (result => {
        setFACValues(result);
      }))
  }, [selectedTask])

  const changePlannedEndDate = () => {
    runInAction(() => {
      const newDate = DateTime.fromISO(formData.plannedEndDate);
      if (newDate.isValid) {
        selectedTask.plannedEndDate = formData.plannedEndDate;
        UpdateTask(project, selectedTask, (result) => {
          let newTask = Task.fromJS(project, result, selectedTask.parent);
          const tasks = selectedTask.getParentTaskPackage().tasks;
          let idx = tasks.findIndex(t => t.id == selectedTask.id);
          tasks[idx] = newTask;
          selectedTask = newTask;
        }).catch((error) => {
          enqueueSnackbar(error.message, { variant: "error" });
        })
      }
    });
  }

  const debouncedHandleChangePlannedEndDate = debounce(changePlannedEndDate, 1000);

  const handleChangePlannedEndDate = (newValue) => {
    formData.plannedEndDate = newValue;
    debouncedHandleChangePlannedEndDate()
  };

  

  const handleChangePlannedCost = (event) => {
    this.props.selectedTask.plannedCost = event.floatValue;
  };

  const handleChangeTaskName = (event) => {
    this.props.selectedTask.name = event.target.value;
  };

  const handleChangeTaskCode = (event) => {
    this.props.selectedTask.code = event.target.value;
  };

  const handleAnalysis = () => {
    this.setState({ selectedTab: "analysis" });
  };

  const handleActual = () => {
    this.setState({ selectedTab: "actual" });
  };

  const handleBaseline = () => {
    this.setState({ selectedTab: "baseline" });
  };

  const handlePeriodWeights = () => {

  };

  return (
    <Paper
      style={{
        minWidth: "1280px",
        minHeight: "400px",
        padding: "8px"
      }}
    >
      <Grid>
        <Typography color="textPrimary" variant="h6">
          Details
        </Typography>
      </Grid>
      <TextField
        key={view.selectedTaskId + "_code"}
        id="task-code"
        label="Code"
        margin="normal"
        value={selectedTask.code}
        onChange={(e) => (runInAction(() => { selectedTask.code = e.target.value }))}
        variant="standard"
        slotProps={{
          input: {
            readOnly: false
          }
        }}
      />
      <TextField
        key={view.selectedTaskId + "_name"}
        id="task-name"
        label="Task Name"
        margin="normal"
        value={selectedTask.name}
        onChange={(e) => (runInAction(() => { selectedTask.name = e.target.value }))}
        variant="standard"
        slotProps={{
          input: {
            readOnly: false
          }
        }}
      />
      <Grid container>
        <ForecastMethodPicker task={selectedTask} />
        <NumericFormat
          key={view.selectedTaskId + "_cost"}
          id="forecast-at-completion"
          label="Forecast At Completion"
          value={FACValues[selectedTask.getRealForecastMethod()]}
          thousandSeparator
          decimalScale={2}
          prefix="$"
          customInput={TextField}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
      </Grid>
      <Typography color="textPrimary" variant="h6">
        Plan
      </Typography>
      <Grid container spacing={1.5}>
        <Grid>
          <NumericFormat
            key={view.selectedTaskId + "_cost"}
            id="planned-cost"
            label="Planned Cost"
            value={selectedTask.plannedCost}
            onValueChange={(e) => (runInAction(() => { selectedTask.plannedCost = e.floatValue }))}
            onBlur={() => updateParentPlannedCost(selectedTask)}
            thousandSeparator
            decimalScale={2}
            prefix="$"
            customInput={TextField}
            variant="standard"
            slotProps={{
              input: {
                readOnly: false
              }
            }}
          />
        </Grid>
        <Grid sx={{ pointerEvents: "none" }}>
          <TextField
            type="number"
            key={view.selectedTaskid + "_duration"}
            id="planned-duration"
            label="Planned Duration"
            value={selectedTask.plannedDuration}
            variant="standard"
            slotProps={{
              input: {
                readOnly: true
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1.5} mt={1.5}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Grid sx={{ ml: 1, maxWidth: "200px" }}>
            
              <DatePicker
                format="yyyy-MM-dd"
                id="planned-start-date-picker"
                label="Planned Start Date"
                value={formData.plannedStartDate}
                onChange={(e) => (runInAction(() => { handleChangePlannedStartDate(); }))}
                slotProps={{
                  textField: { variant: "standard" },
                  readOnly: true
                }}
              />
            
          </Grid>
          <Grid>
            <Observer>{() => (
              <DatePicker
                format="yyyy-MM-dd"
                id="planned-end-date-picker"
                label="Planned End Date"
                value={formData.plannedEndDate}
                onChange={handleChangePlannedEndDate}
                minDate={DateTime.fromISO('2020-01-01')}
                slotProps={{
                  textField: { variant: "standard" },
                  readOnly: true
                }}
              />)}
            </Observer>
          </Grid>
          <Grid>
            <Observer>
              <DatePicker
                format="yyyy-MM-dd"
                id="actual-start-date-picker"
                label="Actual Start Date"
                value={DateTime.fromISO(selectedTask.actualStartDate)}
                onChange={(e) => (runInAction(() => { selectedTask.actualStartDate = e }))}
                slotProps={{
                  textField: { variant: "standard" },
                  readOnly: false
                }}
              />
            </Observer>
          </Grid>
        </LocalizationProvider>
      </Grid>

      <Grid container mt={2} spacing={1.5} sx={{ pointerEvents: "none" }}>
        <TextField
          key={view.selectedTaskId + "_plannedStartPeriod"}
          id="planned-start-period"
          label="Start Period"
          value={selectedTask.plannedStartPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
        <TextField
          key={view.selectedTaskId + "_plannedEndPeriod"}
          id="planned-end-period"
          label="End Period"
          value={selectedTask.plannedEndPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
        <TextField
          key={view.selectedTaskId + "_forecastedEndPeriod"}
          id="forecasted-end-period"
          label="Forecasted End Period"
          value={selectedTask.forecastedEndPeriod}
          variant="standard"
          slotProps={{
            input: {
              readOnly: true
            }
          }}
        />
      </Grid>

      <Typography color="textPrimary" variant="h6">
        Distribution
      </Typography>
      <TaskDistributionPicker task={selectedTask} />

      <hr />
      <ButtonGroup variant="text">
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("actual")}>Actuals</Button>
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("analysis")}>Analysis</Button>
        <Button color="primary" onClick={() => view.setTaskViewSelectedTab("baseline")}>Baselines</Button>
        <Button onClick={() => view.setTaskViewSelectedTab("periodWeights")}>Period Weights</Button>
      </ButtonGroup>

      <Box sx={{ width: "600px" }}>
        <View
          task={selectedTask}
          tab={view.taskViewSelectedTab}
        />
      </Box>
    </Paper>
  )
}

// class TaskPanel extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = {
//       selectedTab: "actual",
//     };
//   }

//   oldDurationValue = this.props.selectedTask.plannedDuration;

//   handleChangePlannedStartDate = (date) => {
//     if (validator.isDate(date, "MM/DD/YYYY")) {
//       let initalEndDate = new Date(this.props.selectedTask.plannedEndDate);
//       let dateDifferential =
//         new Date(date) - new Date(this.props.selectedTask.plannedStartDate);

//       this.props.selectedTask.plannedStartDate = date;
//       this.props.selectedTask.plannedEndDate = new Date(
//         initalEndDate.getTime() + dateDifferential
//       ).toISOString();
//       this.props.selectedTask.updateTask().catch((error) => {
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       });
//     }
//   };

//   handleChangePlannedEndDate = (date) => {
//     if (validator.isDate(date, "MM/DD/YYYY")) {
//       this.props.selectedTask.plannedEndDate = date;
//       this.props.selectedTask.updateTask().catch((error) => {
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       });
//     }
//   };

//   handleChangePlannedCost = (event) => {
//     this.props.selectedTask.plannedCost = event.floatValue;
//   };

//   handleChangeTaskName = (event) => {
//     this.props.selectedTask.name = event.target.value;
//   };

//   handleChangeTaskCode = (event) => {
//     this.props.selectedTask.code = event.target.value;
//   };

//   handleAnalysis = () => {
//     this.setState({ selectedTab: "analysis" });
//   };

//   handleActual = () => {
//     this.setState({ selectedTab: "actual" });
//   };

//   handleBaseline = () => {
//     this.setState({ selectedTab: "baseline" });
//   };

//   render() {
//     return (
//       <Paper style={{ minWidth: "800px", minHeight: "400px", padding: "8px" }}>
//         <Typography color="textPrimary" variant="h6">
//           Details
//         </Typography>
//         <TextField
//           key={this.props.view.selectedTaskId + "_code"}
//           id="taskCode"
//           label="Code"
//           className={this.props.classes.textField}
//           margin="normal"
//           value={this.props.selectedTask.code}
//           InputLabelProps={{
//             shrink: true,
//           }}
//           onChange={this.handleChangeTaskCode}
//         />
//         <TextField
//           key={this.props.view.selectedTaskId + "_name"}
//           id="taskName"
//           label="Task Name"
//           className={this.props.classes.textField}
//           margin="normal"
//           value={this.props.selectedTask.name}
//           InputLabelProps={{
//             shrink: true,
//           }}
//           onChange={this.handleChangeTaskName}
//         />
//         <ForecastMethodPicker task={this.props.selectedTask} />
//         <Typography color="textPrimary" variant="h6">
//           Plan
//         </Typography>
//         <div>
//           <NumericFormat
//             key={this.props.view.selectedTaskId + "_cost"}
//             id="plannedCost"
//             label="Planned Cost"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.plannedCost}
//             onValueChange={this.handleChangePlannedCost}
//             onBlur={() => updateParentPlannedCost(this.props.selectedTask)}
//             thousandSeparator
//             decimalScale={2}
//             prefix="$"
//             customInput={TextField}
//           />
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_duration"}
//             id="plannedDuration"
//             label="Planned Duration"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             InputProps={{ inputProps: { type: "number" } }}
//             value={this.props.selectedTask.plannedDuration}
//           />
//         </div>
//         <div>
//           <LocalizationProvider dateAdapter={AdapterLuxon}>
//             <DatePicker
//               disableToolbar
//               variant="inline"
//               format="MM/dd/yyyy"
//               margin="normal"
//               id="planned-start-date-picker"
//               label="Planned Start Date"
//               value={this.props.selectedTask.plannedStartDate}
//               onChange={this.handleChangePlannedStartDate}
//               KeyboardButtonProps={{
//                 "aria-label": "change date",
//               }}
//             />
//             <DatePicker
//               disableToolbar
//               variant="inline"
//               format="MM/dd/yyyy"
//               margin="normal"
//               id="planned-end-date-picker"
//               label="Planned End Date"
//               value={this.props.selectedTask.plannedEndDate}
//               onChange={(e) => this.handleChangePlannedEndDate(e, true)}
//               KeyboardButtonProps={{
//                 "aria-label": "change date",
//               }}
//             />
//           </LocalizationProvider>
//         </div>
//         <div>
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "plannedStartPeriod"}
//             id="plannedStartPeriod"
//             label="Start Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.plannedStartPeriod}
//           />
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_plannedEndPeriod"}
//             id="plannedEndPeriod"
//             label="End Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.plannedEndPeriod}
//           />
//           <TextField
//             readOnly={true}
//             key={this.props.view.selectedTaskId + "_forecastedEndPeriod"}
//             id="forecastedEndPeriod"
//             label="Forecasted End Period"
//             className={this.props.classes.textField}
//             InputLabelProps={{ shrink: true }}
//             value={this.props.selectedTask.forecastedEndPeriod}
//           />
//         </div>
//         <Typography color="textPrimary" variant="h6">
//           Distribution
//         </Typography>
//         <div>
//           <TaskDistributionPicker
//             task={this.props.selectedTask}
//             classes={this.props.classes}
//           />
//         </div>
//         <hr />
//         <div>
//           <ButtonGroup variant="text" color="primary">
//             <Button onClick={() => this.handleActual()}>Actuals</Button>
//             <Button onClick={() => this.handleAnalysis()}>Analysis</Button>
//             <Button onClick={() => this.handleBaseline()}>Baselines</Button>
//           </ButtonGroup>
//         </div>
//         <div width="600px">
//           <View
//             task={this.props.selectedTask}
//             view={this.state.selectedTab}
//             classes={this.props.classes}
//           />
//         </div>
//       </Paper>
//     );
//   }
// }

// TaskPanel.propTypes = {
//   classes: PropTypes.object.isRequired,
// };

export default observer(TaskPanel);
